import BaseService from '../../base/base_service';
import {SYSTEM_EVENTS, USER_EVENTS} from '../../events/events';
import Event from '../../utils/event';
import _ from '../../utils/underscore';
import Device from '../../utils/device/device';
import IWindowService from './window_service_interface';
import { Check } from '@sparkware/app-utils';

export default class WindowService extends BaseService implements IWindowService {
    static CLASS = 'WindowService';
    private _browserTabId: string;

    init(params) {
        super.init(params);

        this._browserTabId = _.getGuid();

        window.addEventListener('storage', () => {
            Event.fire(SYSTEM_EVENTS.LOCAL_STORAGE_CHANGED);
        });

        window.addEventListener(USER_EVENTS.ON_FOCUS, () => {
            Event.fire(SYSTEM_EVENTS.WINDOW_FOCUS_GAIN);
        });

        window.addEventListener(USER_EVENTS.ON_BLUR, () => {
            Event.fire(SYSTEM_EVENTS.WINDOW_FOCUS_LOSS);
        });


        window.addEventListener('pagehide', () => {
            Event.fire(SYSTEM_EVENTS.ON_BROWSER_PAGE_HIDE);
        });

    }


    getCurrentBrowserTabId() {
        return this._browserTabId;
    }


    getLocationOrigin() {
        return window.location.origin;
    }

    getComputedStyle(element: Element) {
        return window.getComputedStyle(element);
    }

    getComputedSize(element: Element) {
        if (Check.isNullOrUndefined(element)) {
            return {
                width: 0,
                height: 0
            };
        }

        const elementStyle: any = this.getComputedStyle(element);
        return {
            width: elementStyle.width.replace('px', '') * 1,
            height: elementStyle.height.replace('px', '') * 1
        };
    }

    createBlankWindow() {
        return window.open('about:blank', '_blank');
    }

    getPathName(){
        return window.location.pathname;
    }

    getProtocol() {
        return window.location.protocol;
    }

    /**
     * Get the domain hostname.
     */
    getDomain() {
        return window.location.hostname;
    }

    getHost() {
        return window.location.host;
    }

    getHref() {
        return window.location.href;
    }

    /**
     * Refresh the web page.
     *
     */
    reload() {
        window.location.reload();
    }

    /**
     * Get Query String or "string" as Object
     *
     */
    getQueryParameters() {
        const pairs = window.location.search.slice(1).split('&');
        const result = {};

        pairs.forEach(pair => {
            try {
                const pairMatch = pair.match(/([^?=]+)=(([^&]*))/).slice(1);
                result[pairMatch[0]] = decodeURIComponent(pairMatch[1] || '');
            } catch(e) {
                //failed to parse QS
            }
        });

        return JSON.parse(JSON.stringify(result));
    }

    /**
     * Get domain from specific url
     *
     */
    extractDomain(url: string): string {
        let domain: string;
        //find & remove protocol (http, ftp, etc.) and get domain
        if (url.indexOf('://') > -1) {
            domain = url.split('/')[2];
        }
        else {
            domain = url.split('/')[0];
        }

        //find & remove port number
        return domain.split(':')[0];
    }

    private _getLinkProxyExecution() {
        return this.serviceFactory.integrationService().getLinkProxyExecution();
    }

    private get _openInNewWindowHook() {
        return this.serviceFactory.integrationService().openInNewWindowHook();
    }

    /**
     * Redirect the web page to other url.
     */
    redirect(url: string) {

        const linkExecutionProxy = this._getLinkProxyExecution();
        if (_.isFunction(linkExecutionProxy)) {
            linkExecutionProxy(url);
        } else {
            window.location.href = url;
        }

        this.serviceFactory.gameService().closeRunningGame();
    }

    openInNewWindow(url, specs?) {
        const randomHash = Math.random();

        if((Device.hybrid.inCasino || Device.hybrid.isEmbedded) && !this._openInNewWindowHook) {
            return this.serviceFactory.hybridService().openExternalLink(url);
        } else {
            if(this._openInNewWindowHook) {
                return this._openInNewWindowHook(url);
            } else {
                return window.open(url, '_blank'+randomHash, specs);
            }
        }
    }

    getWindowInnerDimensions() {
        return {
            width: window.innerWidth,
            height: window.innerHeight
        };
    }

    getScreenDimensions(){
        return {
            width: window.screen.width,
            height: window.screen.height
        };
    }

    scrollTo(x: number, y: number) {
        window.scrollTo(x, y);
    }

    getGlobalProperty(propertyName: string) {
        return window[propertyName];
    }

    openBankId(url: string): void {
        if(Device.hybrid.inCasino) {
            this.serviceFactory.hybridService().openExternalLink(url);
        } else {
            /*
                BankId url is an url with custom schema something like 'bankid:///?autostarttoken
                This will basically rely on the browser that will ask the operating system to handle bankid schema which in the end will open an application.
                On some browsers this works with window.location.assign and on others with window.open. This is the reason we do those two subsequent calls.
                Only one of them will be succesfull. And even if both will succeed the BankID application is a single instance application so only one instance will be opened.
             */
            window.location.assign(url);
            const tab = window.open(url);
            tab && tab.close();
        }
    }
    getHash(): string {
        return window.location.hash;
    }
}
